import { isImportedRawConditionNode, isLogicContainerNode, type LogicConditionNode, type LogicNode } from '../model';

/** One readable line of an imported condition expression, indented by nesting depth. */
export interface ExpressionLine {
    depth: number;
    text: string;
}

type ExpressionRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is ExpressionRecord => typeof value === 'object' && value !== null && !Array.isArray(value);

/** The imported expression a raw condition carries, from metadata or (older imports) parameters. */
export const readImportedConditionExpression = (node: LogicConditionNode): unknown =>
    node.metadata?.importedConditionExpression ?? node.parameters?.importedConditionExpression;

const formatLiteral = (value: unknown): string => {
    if (Array.isArray(value)) return value.map(formatLiteral).join(', ');
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);

    return String(value);
};

const formatOperand = (operand: unknown, featureLabel: (featureKey: string) => string): string => {
    if (!isRecord(operand)) return formatLiteral(operand);
    if (operand.kind === 'literal') return formatLiteral(operand.value);

    const ref = isRecord(operand.ref) ? operand.ref : operand;

    if (typeof ref.featureKey !== 'string') return typeof operand.kind === 'string' ? operand.kind : JSON.stringify(operand);

    const qualifiers = [
        ...(isRecord(ref.selectors) ? Object.values(ref.selectors).map(formatLiteral) : []),
        ...(typeof ref.duration === 'string' ? [ref.duration] : [])
    ];
    const filters = Array.isArray(ref.filters)
        ? ref.filters.flatMap((filter) =>
              isRecord(filter) && typeof filter.field === 'string'
                  ? [`${filter.field} ${typeof filter.operator === 'string' ? filter.operator : '='} ${formatLiteral(filter.value)}`]
                  : []
          )
        : [];

    return [
        featureLabel(ref.featureKey),
        qualifiers.length > 0 ? ` (${qualifiers.join(', ')})` : '',
        filters.length > 0 ? ` where ${filters.join(' and ')}` : ''
    ].join('');
};

const formatWithin = (within: unknown): string | null => {
    if (!isRecord(within) || typeof within.value !== 'number') return null;

    const unit = within.unit === 'sessions' ? 'trading days' : typeof within.unit === 'string' ? within.unit : '';
    const range = typeof within.minimum === 'number' ? `${within.minimum}–${within.value}` : `${within.value}`;

    return `within ${range} ${unit}${typeof within.duration === 'string' ? ` (${within.duration})` : ''}`.trim();
};

/**
 * An imported condition expression (all / any / not / sequence / comparison, as authored outside
 * the editor) as indented lines of text. Unknown shapes fall back to their kind and raw JSON so
 * nothing is silently dropped. Pure.
 */
export const describeImportedConditionExpression = (
    expression: unknown,
    featureLabel: (featureKey: string) => string
): ExpressionLine[] => {
    const lines: ExpressionLine[] = [];
    const push = (depth: number, text: string) => lines.push({ depth, text });

    const visit = (node: unknown, depth: number, prefix = ''): void => {
        if (!isRecord(node) || typeof node.kind !== 'string') {
            push(depth, `${prefix}${formatLiteral(node)}`);
            return;
        }

        switch (node.kind) {
            case 'all':
            case 'any': {
                const children = Array.isArray(node.children) ? node.children : [];

                if (children.length === 1) {
                    visit(children[0], depth, prefix);
                    return;
                }

                push(depth, `${prefix}${node.kind === 'all' ? 'All of' : 'Any of'}:`);
                children.forEach((child) => visit(child, depth + 1));
                return;
            }
            case 'not':
                push(depth, `${prefix}Not:`);
                visit(node.child, depth + 1);
                return;
            case 'sequence': {
                const steps = Array.isArray(node.steps) ? node.steps : [];

                push(depth, `${prefix}In order${typeof node.policy === 'string' ? ` (${node.policy})` : ''}:`);
                steps.forEach((step, index) => {
                    if (!isRecord(step)) {
                        visit(step, depth + 1, `Step ${index + 1}: `);
                        return;
                    }

                    const within = formatWithin(step.within);
                    const name = typeof step.id === 'string' ? ` ${step.id}` : '';

                    visit(step.when, depth + 1, `Step ${index + 1}${name}${within ? `, ${within}` : ''}: `);

                    if (Array.isArray(step.invalidates) && step.invalidates.length > 0) {
                        push(depth + 2, 'Cancelled if:');
                        step.invalidates.forEach((invalidation) => visit(invalidation, depth + 3));
                    }
                });
                return;
            }
            case 'comparison':
                push(
                    depth,
                    `${prefix}${formatOperand(node.left, featureLabel)} ${typeof node.operator === 'string' ? node.operator : '?'} ${formatOperand(node.right, featureLabel)}`
                );
                return;
            default: {
                const raw = JSON.stringify(node);

                push(depth, `${prefix}${node.kind}: ${raw.length > 160 ? `${raw.slice(0, 157)}…` : raw}`);
            }
        }
    };

    visit(expression, 0);

    return lines;
};

const collectExpressionFeatureKeys = (value: unknown, keys: Set<string>): void => {
    if (Array.isArray(value)) {
        value.forEach((item) => collectExpressionFeatureKeys(item, keys));
        return;
    }

    if (!isRecord(value)) return;
    if (typeof value.featureKey === 'string') keys.add(value.featureKey);

    Object.values(value).forEach((nested) => collectExpressionFeatureKeys(nested, keys));
};

/** Every `featureKey` anywhere in serialized rule JSON (feature refs and imported expressions), sorted. */
export const collectFeatureKeys = (value: unknown): string[] => {
    const keys = new Set<string>();

    collectExpressionFeatureKeys(value, keys);

    return [...keys].sort();
};

/** Every feature key a rule tree reads, from structured conditions and imported expressions, sorted. */
export const collectRuleTreeFeatureKeys = (rootNode: LogicNode): string[] => {
    const keys = new Set<string>();
    const visit = (node: LogicNode) => {
        if (node.kind === 'condition') {
            if (node.featureRef?.featureKey) keys.add(node.featureRef.featureKey);
            if (isImportedRawConditionNode(node)) collectExpressionFeatureKeys(readImportedConditionExpression(node), keys);
        }

        if (isLogicContainerNode(node)) node.children.forEach(visit);
    };

    visit(rootNode);

    return [...keys].sort();
};
